import React, { useEffect, useRef, useState } from 'react';
import { Dialog, DialogTitle, DialogContent, DialogActions, Button, Typography, CircularProgress, Box, MenuItem, Select, FormControl, InputLabel, Snackbar } from '@mui/material';
import type { SelectChangeEvent } from '@mui/material/Select';
import CloseIcon from '@mui/icons-material/Close';

interface WebcamCaptureDialogProps {
  open: boolean;
  onClose: () => void;
  onCapture: (dataUrl: string) => void;
  dialogTitle?: string;
}

const WebcamCaptureDialog: React.FC<WebcamCaptureDialogProps> = ({ open, onClose, onCapture, dialogTitle }) => {
  const videoRef = useRef<HTMLVideoElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const [webcamStream, setWebcamStream] = useState<MediaStream | null>(null);
  const [webcamError, setWebcamError] = useState<string | null>(null);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [devices, setDevices] = useState<MediaDeviceInfo[]>([]);
  const [selectedDeviceId, setSelectedDeviceId] = useState<string | undefined>(undefined);

  // Get available video input devices
  useEffect(() => {
    if (!open) return;
    navigator.mediaDevices.enumerateDevices()
      .then(deviceInfos => {
        const videoDevices = deviceInfos.filter(d => d.kind === 'videoinput');
        setDevices(videoDevices);
        if (videoDevices.length > 0) {
          setSelectedDeviceId(videoDevices[0].deviceId);
        } else {
          setWebcamError('No camera device found.');
          setSnackbarOpen(true);
        }
      })
      .catch(() => {
        setWebcamError('Unable to enumerate devices.');
        setSnackbarOpen(true);
      });
  }, [open]);

  // Start webcam stream
  const startWebcam = async (deviceId?: string) => {
    setLoading(true);
    setWebcamError(null);
    // Stop previous stream
    if (videoRef.current) {
      videoRef.current.srcObject = null;
    }
    if (webcamStream) {
      webcamStream.getTracks().forEach(track => track.stop());
      setWebcamStream(null);
    }
    try {
      const constraints: MediaStreamConstraints = {
        video: deviceId ? { deviceId: { exact: deviceId } } : true
      };
      const stream = await navigator.mediaDevices.getUserMedia(constraints);
      setWebcamStream(stream);
      // Wait for video element to be in DOM before setting srcObject
      setTimeout(() => {
        if (videoRef.current) {
          videoRef.current.srcObject = stream;
        }
      }, 100);
    } catch (err: unknown) {
      if (err && typeof err === 'object' && 'name' in err) {
        const errorName = (err as { name?: string }).name;
        if (errorName === 'NotAllowedError') {
          setWebcamError('Camera access denied. Please allow camera permissions.');
          setSnackbarOpen(true);
        } else if (errorName === 'NotFoundError') {
          setWebcamError('No camera device found.');
          setSnackbarOpen(true);
        } else {
          setWebcamError('Unable to access webcam. Please check permissions and device.');
          setSnackbarOpen(true);
        }
      } else {
        setWebcamError('Unable to access webcam. Please check permissions and device.');
        setSnackbarOpen(true);
      }
    }
    setLoading(false);
  };

  // Start webcam when dialog opens or device changes
  useEffect(() => {
    if (open && selectedDeviceId) {
      // Delay to ensure video element is mounted
      setTimeout(() => {
        startWebcam(selectedDeviceId);
      }, 100);
    }
    // Cleanup on close
    const videoEl = videoRef.current; // Copy ref to local variable for cleanup
    return () => {
      if (videoEl) {
        videoEl.srcObject = null;
      }
      if (webcamStream) {
        webcamStream.getTracks().forEach(track => track.stop());
        setWebcamStream(null);
      }
    };
    // eslint-disable-next-line
  }, [open, selectedDeviceId]);


  const handleCapture = () => {
    if (canvasRef.current && videoRef.current) {
      const context = canvasRef.current.getContext('2d');
      if (context) {
        canvasRef.current.width = videoRef.current.videoWidth;
        canvasRef.current.height = videoRef.current.videoHeight;
        context.drawImage(videoRef.current, 0, 0, videoRef.current.videoWidth, videoRef.current.videoHeight);
        const dataUrl = canvasRef.current.toDataURL('image/png');
        onCapture(dataUrl);
      }
    }
  };

  const handleDeviceChange = (event: SelectChangeEvent<string>) => {
    setSelectedDeviceId(event.target.value as string);
  };

  const handleRetry = () => {
    // If there are no devices, keep showing the error
    if (devices.length === 0) {
      setWebcamError('No camera device found.');
      setSnackbarOpen(true);
      return;
    }
    setWebcamError(null);
    if (selectedDeviceId) {
      startWebcam(selectedDeviceId);
    }
  };

  // Snackbar close handler (must be in component scope)
  const handleSnackbarClose = (_event?: React.SyntheticEvent | Event, reason?: string) => {
    if (reason === 'clickaway') return;
    setSnackbarOpen(false);
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth sx={{ m: 1 }}>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', pr: 2 }}>
        {dialogTitle || 'Capture Image from Webcam'}
        <Button onClick={onClose} sx={{ minWidth: 0, p: 0, ml: 2 }}>
          <CloseIcon />
        </Button>
      </DialogTitle>
      <DialogContent>
        {devices.length > 1 && (
          <FormControl fullWidth size="small" sx={{ mb: 2 }}>
            <InputLabel id="camera-select-label">Camera</InputLabel>
            <Select
              labelId="camera-select-label"
              value={selectedDeviceId || ''}
              label="Camera"
              onChange={handleDeviceChange}
              disabled={loading}
            > 
              {devices.map(device => (
                <MenuItem key={device.deviceId} value={device.deviceId}>
                  {device.label || `Camera ${device.deviceId.slice(-4)}`}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        )}
        {loading ? (
          <Box display="flex" justifyContent="center" alignItems="center" minHeight={200}>
            <CircularProgress />
          </Box>
        ) : devices.length === 0 ? (
          <Box display="flex" flexDirection="column" alignItems="center" minHeight={200}>
            <Typography color="error" sx={{ mb: 2 }}>No camera device found.</Typography>
            <Button variant="outlined" onClick={handleRetry}>Retry</Button>
          </Box>
        ) : webcamError ? (
          <Box display="flex" flexDirection="column" alignItems="center" minHeight={200}>
            <Typography color="error" sx={{ mb: 2 }}>{webcamError}</Typography>
            <Button variant="outlined" onClick={handleRetry}>Retry</Button>
          </Box>
        ) : (
          <video ref={videoRef} autoPlay style={{ width: '100%', borderRadius: 8, background: '#000' }} />
        )}
        <canvas ref={canvasRef} style={{ display: 'none' }} />
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} sx={{ textTransform: 'none' }}>Cancel</Button>
        <Button
          variant="contained"
          onClick={handleCapture}
          sx={{ background: '#E17E61', color: '#fff', mb: 1, textTransform: 'none' }}
          disabled={!!webcamError || loading || !webcamStream}
        >
          Capture
        </Button>
      </DialogActions>
      {/* Snackbar for webcam errors */}
      <Snackbar
        open={snackbarOpen && !!webcamError}
        autoHideDuration={4000}
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        message={webcamError}
      />
    </Dialog>
  );
}; 

export default WebcamCaptureDialog;
